import type {
  EmbeddingProviderId,
  EmbeddingProviderLoadResult,
} from '@/versions/v1/shared/admin-seed';

export const EMBEDDING_DIM = 1024;

export const EMBEDDING_REEMBED_ESTIMATE = {
  MS_PER_VECTOR: 25,
  COST_PER_1K_USD: 0.1,
} as const;

export function estimateReembed(totalVectors: number): {
  minutes: number;
  costUsd: number;
} {
  const minutes = Math.ceil(
    (totalVectors * EMBEDDING_REEMBED_ESTIMATE.MS_PER_VECTOR) / 1000 / 60,
  );
  const costUsd =
    (totalVectors / 1000) * EMBEDDING_REEMBED_ESTIMATE.COST_PER_1K_USD;
  return { minutes, costUsd };
}

export type EmbeddingCatalogEntry = {
  provider: EmbeddingProviderId;
  model: string;
  label: string;
  dim: number;
  selectable: boolean;
};

const CATALOG_RAW: readonly Omit<EmbeddingCatalogEntry, 'selectable'>[] = [
  {
    provider: 'voyage',
    model: 'voyage-3',
    label: 'Voyage voyage-3',
    dim: 1024,
  },
  {
    provider: 'gemini',
    model: 'gemini-embedding-001',
    label: 'Gemini gemini-embedding-001',
    dim: 1024,
  },
  {
    provider: 'bedrock',
    model: 'amazon.titan-embed-text-v2:0',
    label: 'Bedrock Titan Text v2',
    dim: 1024,
  },
  {
    provider: 'bedrock',
    model: 'cohere.embed-english-v3',
    label: 'Bedrock Cohere Embed English v3',
    dim: 1024,
  },
  {
    provider: 'bedrock',
    model: 'cohere.embed-multilingual-v3',
    label: 'Bedrock Cohere Embed Multilingual v3',
    dim: 1024,
  },
  {
    provider: 'voyage',
    model: 'voyage-3-lite',
    label: 'Voyage voyage-3-lite',
    dim: 512,
  },
  {
    provider: 'bedrock',
    model: 'amazon.titan-embed-text-v1',
    label: 'Bedrock Titan Text v1',
    dim: 1536,
  },
] as const;

export const EMBEDDING_MODEL_CATALOG: readonly EmbeddingCatalogEntry[] =
  CATALOG_RAW.map((entry) => ({
    ...entry,
    selectable: entry.dim === EMBEDDING_DIM,
  }));

export const PROVIDER_ORDER: EmbeddingProviderId[] = ['voyage', 'gemini', 'bedrock'];

export const PROVIDER_LABELS: Record<EmbeddingProviderId, string> = {
  voyage: 'Voyage',
  gemini: 'Gemini',
  bedrock: 'Bedrock',
};

export type LogoProvider = 'voyage' | 'google' | 'bedrock';

export const PROVIDER_LOGO_ID: Record<EmbeddingProviderId, LogoProvider> = {
  voyage: 'voyage',
  gemini: 'google',
  bedrock: 'bedrock',
};

export function catalogByProvider(
  provider: EmbeddingProviderId,
): EmbeddingCatalogEntry[] {
  return EMBEDDING_MODEL_CATALOG.filter((entry) => entry.provider === provider);
}

export function isEmbeddingConfigDirty(
  loadResult: EmbeddingProviderLoadResult,
  pickedProvider: EmbeddingProviderId,
  pickedModel: string,
): boolean {
  return (
    pickedProvider !== loadResult.active.provider ||
    pickedModel !== loadResult.active.model
  );
}

export function totalStaleVectors(loadResult: EmbeddingProviderLoadResult): number {
  return loadResult.staleMemoryVectors + loadResult.staleProjectFileVectors;
}

export function pickedTag(provider: EmbeddingProviderId, model: string): string {
  return `${provider}:${model}`;
}
